import type { ReactNode } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { formatDate } from "@/lib/format-date";
import { personDisplayName } from "@/lib/person-display";
import { MatchModeLabel } from "./MatchModeLabel";
import type { MatchMode } from "@/graphql/generated/operations";

// Die Kachel eines Matches: Modus, Name, wer es angelegt hat, Beschreibung,
// darunter Datum und Kandidatenzahl.
//
// Lag als Markup in der Match-Übersicht, bis ein zweiter und dritter Aufrufer
// kam (das Projekt zeigt seine verknüpften Matches an zwei Stellen). Ein Match
// soll überall gleich aussehen, sonst lernt man die Kachel dreimal.
//
// `extra` ist der Platz für Zusätze in der Fusszeile, die eine eigene Abfrage
// brauchen - die Ergebniszahl der Übersicht etwa. Die Kachel selbst fragt
// nichts nach, damit eine Liste von zwanzig nicht zwanzig Abfragen auslöst.
//
// `onSelect` macht aus der Kachel eine AUSWAHL statt eines Weges: der Klick
// öffnet dann etwas auf derselben Seite, statt zum Match zu springen. Das
// Projekt braucht das, wenn es in einer Liste von Matches eines herausgreift,
// um dessen Ergebnisse darunter zu zeigen - dort wäre Wegnavigieren genau das
// Falsche, man will ja hierbleiben und übernehmen.

export interface MatchCardMatch {
  id: string;
  name?: string | null;
  description?: string | null;
  matchMode?: MatchMode | null;
  updatedAt?: string | null;
  candidateCount?: number | null;
  createdBy?: { name?: { given?: (string | null)[] | null; family?: string | null } | null; formattedName?: string | null } | null;
}

export function MatchCard({
  match,
  extra,
  id,
  onSelect,
  selected = false,
}: {
  match: MatchCardMatch;
  extra?: ReactNode;
  id?: string;
  /** Gesetzt: der Klick wählt aus, statt zum Match zu navigieren. */
  onSelect?: () => void;
  selected?: boolean;
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  // `personDisplayName` fällt auf "?" zurück, die Karte zeigt seit jeher einen
  // Strich. Der Unterschied ist keine Kosmetik: "?" liest sich wie eine Frage an
  // den Betrachter, der Strich wie eine leere Stelle, und eine leere Stelle ist es.
  const creator = match.createdBy ? personDisplayName(match.createdBy) : "";
  const creatorLabel = creator && creator !== "?" ? creator : "–";

  const meta = [
    formatDate(match.updatedAt, intl.locale) || null,
    intl.formatMessage(
      { id: "match.landing.candidatesCount", defaultMessage: "{count} candidates" },
      { count: match.candidateCount ?? 0 },
    ),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <button
      id={id ?? `match-card-${match.id}`}
      type="button"
      aria-pressed={onSelect ? selected : undefined}
      onClick={() => (onSelect ? onSelect() : navigate(`/match/${match.id}`))}
      className={`box bg-surface p-4 text-left transition hover:ring-accent-decoration/40 ${
        selected ? "smycles-selected ring-2 ring-accent-decoration" : ""
      }`}
    >
      <div className="text-smycles-micro tracking-[0.2em] text-ink-muted">
        <MatchModeLabel mode={match.matchMode} />
      </div>
      <div className="mt-0.5 text-smycles-body font-medium">{match.name}</div>
      {/* Der Name wird aus seinen Teilen gebaut, nicht nur aus `formattedName`:
          nicht jede Abfrage liefert beides, und wer nur eines nimmt, zeigt bei
          der anderen einen Strich. Platzhalterstrich statt leerer Zeile, sonst
          rutscht die Beschreibung hoch und die Kacheln einer Reihe stehen
          unterschiedlich hoch. */}
      <div className="mt-1 text-smycles-label text-ink-muted">
        {creatorLabel}
      </div>
      {match.description ? (
        <div className="mt-2 line-clamp-3 text-smycles-meta leading-snug text-ink">{match.description}</div>
      ) : null}
      <div className="mt-4 text-smycles-micro text-ink-muted">
        {meta}
        {extra}
      </div>
    </button>
  );
}
